import React, { useState, useEffect } from 'react';
import { Utils, authJsonHeaders, formatDateTime, handleSessionExpired } from '../../utils';
import { toast } from 'react-toastify';
import { MdSearch, MdCheck, MdClose, MdVisibility, MdPending, MdCheckCircle, MdCancel, MdRefresh } from 'react-icons/md';
import Modal from '../common/Modal';
import ListPaginationBar from '../common/ListPaginationBar';

const AdminRegistrationRequests = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('pending');
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [rejectModal, setRejectModal] = useState({ show: false, request: null, reason: '' });
    const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });

    useEffect(() => {
        fetchStats();
    }, []);

    useEffect(() => {
        fetchRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page, search, filter]);

    const fetchStats = async () => {
        try {
            const response = await fetch(Utils.baseurl + 'admin/registration-requests/stats', {
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) setStats(result.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const response = await fetch(Utils.baseurl + 'admin/registration-requests', {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ page: pagination.page, limit: pagination.limit, search, status: filter })
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) {
                setRequests(result.data);
                setPagination(prev => ({ ...prev, ...result.pagination }));
            }
        } catch (error) {
            console.error('Error fetching requests:', error);
        }
        setLoading(false);
    };

    const handleApprove = async (requestId) => {
        setActionLoading(true);
        try {
            const response = await fetch(Utils.baseurl + 'admin/registration-requests/approve', {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ requestId })
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) {
                fetchRequests();
                fetchStats();
                setShowModal(false);


                toast.success(result.message || 'User approved successfully! They can now sign in.');
            } else {
                toast.error(result.message || 'Failed to approve');
            }
        } catch (error) {
            console.error('Error approving:', error);
            toast.error('Error approving request');
        }
        setActionLoading(false);
    };

    const handleReject = async () => {
        if (!rejectModal.request) return;
        setActionLoading(true);
        try {
            const response = await fetch(Utils.baseurl + 'admin/registration-requests/reject', {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({
                    requestId: rejectModal.request.id,
                    reason: rejectModal.reason
                })
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) {
                fetchRequests();
                fetchStats();
                setRejectModal({ show: false, request: null, reason: '' });
                toast.success('Request rejected');
            } else {
                toast.error(result.message || 'Failed to reject');
            }
        } catch (error) {
            console.error('Error rejecting:', error);
            toast.error('Error rejecting request');
        }
        setActionLoading(false);
    };

    const viewDetails = (request) => {
        setSelectedRequest(request);
        setShowModal(true);
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return formatDateTime(date) || 'N/A';
    };

    const getStatusBadge = (status) => {
        const styles = {
            pending: { bg: '#fef3c7', color: '#92400e', icon: <MdPending size={14} /> },
            approved: { bg: '#dcfce7', color: '#166534', icon: <MdCheckCircle size={14} /> },
            rejected: { bg: '#fee2e2', color: '#991b1b', icon: <MdCancel size={14} /> }
        };
        const s = styles[status] || styles.pending;
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500', background: s.bg, color: s.color }}>
                {s.icon} {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    const StatCard = ({ icon, title, value, color }) => (
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', flex: 1, minWidth: '150px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ background: color + '20', padding: '10px', borderRadius: '10px' }}>{icon}</div>
                <div>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a1a' }}>{value}</div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>{title}</div>
                </div>
            </div>
        </div>
    );

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                    <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>Registration Requests</h1>
                    <p style={{ color: '#6b7280', marginTop: '8px' }}>Review and approve new user registrations</p>
                </div>
                <button onClick={() => { fetchRequests(); fetchStats(); }} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
                    <MdRefresh size={18} /> Refresh
                </button>
            </div>

            {/* Stats */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <StatCard icon={<MdPending size={24} color="#d97706" />} title="Pending" value={stats.pending} color="#d97706" />
                <StatCard icon={<MdCheckCircle size={24} color="#059669" />} title="Approved" value={stats.approved} color="#059669" />
                <StatCard icon={<MdCancel size={24} color="#dc2626" />} title="Rejected" value={stats.rejected} color="#dc2626" />
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {['pending', 'approved', 'rejected', 'all'].map(f => (
                        <button
                            key={f}
                            onClick={() => { setFilter(f); setPagination(p => ({ ...p, page: 1 })); }}
                            style={{
                                padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
                                background: filter === f ? '#4f46e5' : 'white',
                                color: filter === f ? 'white' : '#6b7280',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}
                        >
                            {f.charAt(0).toUpperCase() + f.slice(1)}
                        </button>
                    ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'white', padding: '8px 16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <MdSearch size={20} color="#9ca3af" />
                    <input
                        type="text"
                        placeholder="Search by email or company..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
                        style={{ border: 'none', outline: 'none', fontSize: '14px', width: '220px' }}
                    />
                </div>
            </div>

            {/* Table */}
            <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
                        <thead>
                            <tr style={{ background: '#f9fafb' }}>
                                <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '13px' }}>Applicant</th>
                                <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '13px' }}>Company</th>
                                <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '13px' }}>Industry</th>
                                <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '13px' }}>Status</th>
                                <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '13px' }}>Submitted</th>
                                <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '13px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</td></tr>
                            ) : requests.length === 0 ? (
                                <tr><td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>No requests found</td></tr>
                            ) : requests.map(req => (
                                <tr key={req.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                    <td style={{ padding: '14px 16px' }}>
                                        <div style={{ fontWeight: '500', color: '#1a1a1a' }}>{req.first_name} {req.last_name}</div>
                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{req.email}</div>
                                    </td>
                                    <td style={{ padding: '14px 16px' }}>
                                        <div style={{ fontWeight: '500', color: '#1a1a1a' }}>{req.company_name}</div>
                                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{req.company_size}</div>
                                    </td>
                                    <td style={{ padding: '14px 16px', color: '#374151', fontSize: '13px' }}>{req.industry}</td>
                                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>{getStatusBadge(req.status)}</td>
                                    <td style={{ padding: '14px 16px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>{formatDate(req.createdAt)}</td>
                                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                                        <button onClick={() => viewDetails(req)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4f46e5', marginRight: '8px' }} title="View Details">
                                            <MdVisibility size={20} />
                                        </button>
                                        {req.status === 'pending' && (
                                            <>
                                                <button onClick={() => handleApprove(req.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#059669', marginRight: '8px' }} title="Approve">
                                                    <MdCheck size={20} />
                                                </button>
                                                <button onClick={() => setRejectModal({ show: true, request: req, reason: '' })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }} title="Reject">
                                                    <MdClose size={20} />
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <ListPaginationBar
                    page={pagination.page}
                    setPage={(n) => setPagination((p) => ({ ...p, page: n }))}
                    total={pagination.total}
                    pageSize={pagination.limit}
                    totalPages={pagination.totalPages}
                    ariaLabel="Admin registration requests pagination"
                    className="px-3 py-3"
                    style={{ borderTop: '1px solid #f3f4f6' }}
                    loading={loading}
                />
            </div>

            {/* Detail Modal */}
            {showModal && selectedRequest && (
                <Modal
                    title={<span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}><span>Registration Details</span>{getStatusBadge(selectedRequest.status)}</span>}
                    onClose={() => setShowModal(false)}
                    maxWidth="600px"
                    footer={<>
                        <button type="button" onClick={() => setShowModal(false)} style={{ padding: '10px 18px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Close</button>
                        {selectedRequest.status === 'pending' && (
                            <>
                                <button type="button" onClick={() => { setShowModal(false); setRejectModal({ show: true, request: selectedRequest, reason: '' }); }} style={{ padding: '10px 18px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Reject</button>
                                <button type="button" onClick={() => handleApprove(selectedRequest.id)} disabled={actionLoading} style={{ padding: '10px 18px', background: '#059669', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                                    {actionLoading ? 'Processing...' : 'Approve'}
                                </button>
                            </>
                        )}
                    </>}
                >
                        <div style={{ background: '#f9fafb', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
                            <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#6b7280', textTransform: 'uppercase' }}>Personal Info</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
                                <div><span style={{ color: '#6b7280' }}>Name:</span> <strong>{selectedRequest.first_name} {selectedRequest.last_name}</strong></div>
                                <div><span style={{ color: '#6b7280' }}>Email:</span> <strong>{selectedRequest.email}</strong></div>
                                <div><span style={{ color: '#6b7280' }}>Phone:</span> <strong>{selectedRequest.phone || 'N/A'}</strong></div>
                                <div><span style={{ color: '#6b7280' }}>Role:</span> <strong>{selectedRequest.category}</strong></div>
                            </div>
                        </div>

                        <div style={{ background: '#eff6ff', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
                            <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#1d4ed8', textTransform: 'uppercase' }}>Company Info</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
                                <div><span style={{ color: '#6b7280' }}>Company:</span> <strong>{selectedRequest.company_name}</strong></div>
                                <div><span style={{ color: '#6b7280' }}>Website:</span> <strong>{selectedRequest.website || 'N/A'}</strong></div>
                                <div><span style={{ color: '#6b7280' }}>Industry:</span> <strong>{selectedRequest.industry}</strong></div>
                                <div><span style={{ color: '#6b7280' }}>Size:</span> <strong>{selectedRequest.company_size}</strong></div>
                            </div>
                        </div>

                        <div style={{ background: '#f0fdf4', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
                            <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#166534', textTransform: 'uppercase' }}>Application</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
                                <div><span style={{ color: '#6b7280' }}>App Name:</span> <strong>{selectedRequest.app_name}</strong></div>
                                <div><span style={{ color: '#6b7280' }}>Channel:</span> <strong>{selectedRequest.channel}</strong></div>
                            </div>
                        </div>

                        {selectedRequest.rejection_reason && (
                            <div style={{ background: '#fef2f2', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
                                <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#991b1b', textTransform: 'uppercase' }}>Rejection Reason</h3>
                                <p style={{ margin: 0, color: '#991b1b' }}>{selectedRequest.rejection_reason}</p>
                            </div>
                        )}

                </Modal>
            )}

            {/* Reject Modal */}
            {rejectModal.show && (
                <Modal
                    title="Reject Registration"
                    onClose={() => setRejectModal({ show: false, request: null, reason: '' })}
                    maxWidth="450px"
                    footer={<>
                        <button type="button" onClick={() => setRejectModal({ show: false, request: null, reason: '' })} style={{ padding: '10px 18px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                        <button type="button" onClick={handleReject} disabled={actionLoading} style={{ padding: '10px 18px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                            {actionLoading ? 'Rejecting...' : 'Reject'}
                        </button>
                    </>}
                >
                        <p style={{ color: '#6b7280', marginBottom: '16px' }}>
                            Rejecting request from <strong>{rejectModal.request?.email}</strong>
                        </p>
                        <textarea
                            placeholder="Reason for rejection (optional)"
                            value={rejectModal.reason}
                            onChange={(e) => setRejectModal(prev => ({ ...prev, reason: e.target.value }))}
                            style={{ width: '100%', padding: '12px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', minHeight: '100px', resize: 'vertical', boxSizing: 'border-box' }}
                        />
                </Modal>
            )}
        </div>
    );
};

export default AdminRegistrationRequests;
